import { Box, Button, Checkbox, Link, MenuItem, Select, TableCell as MuiTableCell, TableRow, Tooltip, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import moment from 'moment-timezone';
import React, { Fragment, useCallback } from 'react';
import { CrownIcon } from '../../../assets/Svg';
import settings from '../../../services/settings';
import { CommentReviewStatus, IComment, ICommentReview } from '../../../services/types';
import { RouteNames } from '../../config';
import useStyles from './Comment.styles';
import CommentUser from './CommentUser';

interface IProps {
  isBannedUserMode: boolean;
  isReported: boolean;
  comment: IComment;
  isChecked: boolean;
  disableReviewStatusChange: boolean;
  onCheckboxChange: (comment: IComment) => void;
  onReviewStatusChange: (commentReview: ICommentReview, status: CommentReviewStatus) => void;
  onReportClick: (comment: IComment) => void;
  onInternalNoteClick: (comment: IComment) => void;
}

const TableCell = withStyles({
  root: {
    verticalAlign: 'top',
    paddingTop: 16,
    paddingBottom: 16,
  },
})(MuiTableCell);

const CommentTableRow = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const {
    isBannedUserMode,
    isReported,
    comment,
    isChecked,
    disableReviewStatusChange,
    onCheckboxChange,
    onReviewStatusChange,
    onReportClick,
    onInternalNoteClick,
  } = props;

  const isDisabledByUserBanned = isBannedUserMode === false && comment.isBannedUser;

  const handleCheckboxChange = useCallback(() => {
    onCheckboxChange(comment);
  }, [comment, onCheckboxChange]);

  const handleReviewStatusChange = useCallback(
    e => {
      if (!comment.review) {
        return;
      }

      onReviewStatusChange(comment.review, e.target.value as CommentReviewStatus);
    },
    [comment, onReviewStatusChange],
  );

  const handleReportClick = useCallback(() => {
    onReportClick(comment);
  }, [comment, onReportClick]);

  const handleInternalNoteClick = useCallback(() => {
    onInternalNoteClick(comment);
  }, [comment, onInternalNoteClick]);

  return (
    <TableRow key={comment.id} hover className={cx({ [classes.dimmed]: isDisabledByUserBanned })}>
      {!isBannedUserMode && (
        <TableCell padding="checkbox">
          <Checkbox checked={isChecked} color="primary" onChange={handleCheckboxChange} disabled={isDisabledByUserBanned} />
        </TableCell>
      )}
      <TableCell padding={!isBannedUserMode ? 'checkbox' : 'default'}>
        <Box className={classes.tableCell80}>
          <Tooltip title="Comment Unique number">
            <Typography className={classes.contentId}>{comment.id}</Typography>
          </Tooltip>
          <Typography className={classes.contentDesc2}>
            {moment(isReported ? comment.lastReportedAt : comment.createdAt).format('ll HH:mm:ss')}
          </Typography>
        </Box>
      </TableCell>
      <TableCell className={classes.tableCell80} padding="checkbox">
        <CommentUser userId={comment.userId} displayName={comment.displayName} username={comment.username} isWriter={comment.isWrittenByWriter} />
      </TableCell>
      <TableCell className={classes.tabelCellContent}>
        <Box>
          <Link href={`${settings.getCrewURI()}/stories/${comment.storyId}`} target="_blank" className={classes.contentLink}>
            {`Story-${comment.storyId}`}
          </Link>
          /
          <Link
            href={`${settings.getCrewURI()}/stories/${comment.storyId}/seasons/${comment.seasonId}`}
            target="_blank"
            className={classes.contentLink}
          >
            {`Season-${comment.seasonId}`}
          </Link>
          /
          <Link
            href={`${settings.getCrewURI()}/stories/${comment.storyId}/seasons/${comment.seasonId}/episodes/${comment.episodeId}`}
            target="_blank"
            className={classes.contentLink}
          >
            {`Episode #${comment.episodeNo}`}
          </Link>
          {Boolean(comment.parentId) && (
            <Fragment>
              /
              <Link
                href={`${RouteNames.Moderation}/all?commentId=${comment.parentId}&startDate=2020-01-01&endDate=2099-01-01&reviewStatus=all`}
                className={classes.contentLink}
              >
                {comment.parentId}
              </Link>
            </Fragment>
          )}
          <Typography variant="body2">{comment.originalContents}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box className={classes.tableCell120}>
          <Select
            disableUnderline={true}
            value={comment.isBannedUser ? CommentReviewStatus.HiddenByBan : comment.review?.status}
            onChange={handleReviewStatusChange}
            inputProps={{
              className: cx({
                [classes.contentReviewStatus]: true,
                [classes.contentApproved]: comment.review?.status === CommentReviewStatus.Approved && !comment.isBannedUser,
                [classes.contentEmphasize]:
                  (!comment.isBannedUser && comment.review?.status === CommentReviewStatus.Hidden) || (comment.isBannedUser && isBannedUserMode),
              }),
            }}
            classes={{
              icon: classes.contentSelectIcon,
            }}
            disabled={disableReviewStatusChange || isDisabledByUserBanned}
          >
            {comment.isBannedUser && [
              <MenuItem key={`1-1`} value={CommentReviewStatus.HiddenByBan}>
                Banned (Global)
              </MenuItem>,
              <MenuItem key={`1-2`} value={CommentReviewStatus.Unban} className={classes.contentEmphasize}>
                Unban
              </MenuItem>,
            ]}
            {!comment.isBannedUser && [
              <MenuItem key={`2-1`} value={CommentReviewStatus.NotReviewed}>
                Not reviewed
              </MenuItem>,
              <MenuItem key={`2-2`} value={CommentReviewStatus.Approved}>
                Approved
              </MenuItem>,
              <MenuItem key={`2-3`} value={CommentReviewStatus.Pending}>
                Pending
              </MenuItem>,
              <MenuItem key={`2-4`} value={CommentReviewStatus.Hidden} className={classes.contentEmphasize}>
                Hidden
              </MenuItem>,
              <MenuItem key={`2-5`} value={CommentReviewStatus.HiddenByBan} className={classes.contentEmphasize}>
                Banned (Global)
              </MenuItem>,
            ]}
          </Select>
          {comment.review?.status !== CommentReviewStatus.NotReviewed && !!comment.review?.updatedAt && (
            <Typography className={classes.contentDesc2}>
              {`${moment(comment.review?.updatedAt).format('ll HH:mm:ss')} (${comment.review.adminUsername})`}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {comment.reportedCount > 0 && !!comment.reportReason && (
          <Link component="div" className={classes.contentReportedLink} onClick={handleReportClick}>
            {comment.reportedCount > 1 && <Typography className={classes.contentReportedCount}>{`(+${comment.reportedCount - 1})`}</Typography>}
            <Typography className={classes.contentReportReason}>
              {comment.isReportedByWriter && (
                <span className={classes.crownIcon}>
                  <CrownIcon color={'#d0021b'} />
                </span>
              )}
              {`${comment.reportReason}`}
            </Typography>
          </Link>
        )}
      </TableCell>
      <TableCell>
        <Button
          size="small"
          classes={{
            root: cx({
              [classes.contentNotePlaceholder]: true,
              [classes.contentNoteHasContent]: Boolean(comment.review?.note),
            }),
          }}
          onClick={handleInternalNoteClick}
          disabled={isDisabledByUserBanned}
        >
          {Boolean(comment.review?.note) ? comment.review?.note : 'Enter details'}
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default React.memo(CommentTableRow);
